import React, { useEffect, useState } from 'react';
import styles from 'src/app/components/FileTable/fileTable.module.scss';
import { PropsType, compatibleTypes } from 'src/app/components/FileTable/fileTable.config';
import {
  DeleteOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileZipOutlined,
  FileJpgOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  EyeOutlined,
  FileWordOutlined,
  FilePptOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';

import { useSelector } from 'react-redux';
import { reduxRootTypes } from 'src/types';
import { attachmentService } from 'src/app/services';
import { BiSolidFileJson } from 'react-icons/bi';
import { RiFileVideoLine } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';
import renderPreviewContent from './renderPreviewContent';
import Popover from 'antd/es/popover';
import Tooltip from 'antd/es/tooltip';
import Modal from 'antd/es/modal/Modal';
import Table from 'antd/es/table/Table';

const FileTable: React.FC<PropsType> = ({ dataSrc, deleteRow, attachments, disableTable, disableLimit }) => {
  const { t } = useTranslation();

  const userState = useSelector((state: reduxRootTypes.RootStateType) => state.userState);

  const [filteredDataSrc, setFilteredDataSrc] = useState<[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<any>(null);

  useEffect(() => {
    const newFilteredDataSrc = dataSrc.filter((record: any) =>
      compatibleTypes.includes(record.mediaType || record.file.type),
    );
    setFilteredDataSrc(newFilteredDataSrc);
  }, [dataSrc]);

  const handlePreview = (record: any) => {
    setPreviewFile(record);
    setPreviewVisible(true);
  };

  const columns = [
    {
      title: !disableLimit ? (
        <Popover content={t('general.maxThreeAttachment')} placement="bottom" trigger="hover">
          <InfoCircleOutlined style={{ fontSize: '18px', color: 'var(--ete-primary-green-color)' }} />
        </Popover>
      ) : undefined,
      dataIndex: 'typeIcon',
      key: 'typeIcon',
      render: (text: any, record: any) => {
        switch (record.mediaType || record.file.type) {
          case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            return (
              <span>
                <FileWordOutlined className={styles.fileIcon} />
              </span>
            );

          case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
          case 'application/vnd.ms-powerpoint':
            return (
              <span>
                <FilePptOutlined className={styles.fileIcon} />
              </span>
            );

          case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            return (
              <span>
                <FileExcelOutlined className={styles.fileIcon} />
              </span>
            );

          case 'image/png':
            return (
              <span>
                <FileImageOutlined className={styles.fileIcon} />
              </span>
            );

          case 'image/jpeg':
            return (
              <span>
                <FileJpgOutlined className={styles.fileIcon} />
              </span>
            );

          case 'image/jpg':
            return (
              <span>
                <FileJpgOutlined className={styles.fileIcon} />
              </span>
            );

          case 'application/pdf':
            return (
              <span>
                <FilePdfOutlined className={styles.fileIcon} />
              </span>
            );

          case 'application/zip':
            return (
              <span>
                <FileZipOutlined className={styles.fileIcon} />
              </span>
            );
          case 'application/json':
            return (
              <span>
                <BiSolidFileJson className={styles.fileIcon} />
              </span>
            );
          case 'text/plain':
            return (
              <span>
                <FileTextOutlined className={styles.fileIcon} />
              </span>
            );
          case 'video/mp4':
            return (
              <span>
                <RiFileVideoLine className={styles.fileIcon} />
              </span>
            );

          default:
            return null;
        }
      },
    },
    {
      title: t('general.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text: any, record: any) => {
        const fileName = record.name !== undefined ? record.name : record.file.name;
        return (
          <Tooltip title={fileName} className={styles.tableTitleStyle} zIndex={10000}>
            <span>{fileName.length > 15 ? `${fileName.slice(0, 15)}...` : fileName}</span>
          </Tooltip>
        );
      },
    },
    {
      title: t('general.size'),
      dataIndex: 'size',
      key: 'size',
      render: (text: any, record: any) => {
        const sizeInMb: any =
          record.size !== undefined
            ? (record.size / (1024 * 1024)).toFixed(5)
            : (record.file.size / (1024 * 1024)).toFixed(5);
        return `${sizeInMb} MB`;
      },
    },
    {
      title: t('general.type'),
      dataIndex: 'mediaType',
      key: 'mediaType',

      render: (text: any, record: any) => {
        switch (record.mediaType || record.file.type) {
          case 'image/jpeg':
          case 'image/jpg':
          case 'image/png':
            return <span>image</span>;

          case 'application/zip':
            return <span>compressed</span>;

          case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          case 'text/plain':
          case 'application/msword':
          case 'application/pdf':
            return <span>document</span>;

          case 'application/json':
            return <span>json</span>;

          case 'video/mp4':
            return <span>video</span>;

          case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
          case 'application/vnd.ms-powerpoint':
            return <span>presentation</span>;

          default:
            return null;
        }
      },
    },
    {
      title: t('general.createdAt'),
      dataIndex: 'uploadedAt',
      key: 'uploadedAt',
      render: (text: any, record: any) => {
        if (record?.uploadedAt !== undefined && record?.uploadedAt?.length > 2) {
          const splitDay = record.uploadedAt.slice(0, 10);
          const splitHour = record.uploadedAt.slice(11, 19);
          return `${splitDay} ${splitHour}`;
        } else {
          // const splitDay = record.file.lastModifiedDate.slice(0, 10);
          // const splitHour = record.file.lastModifiedDate.slice(11, 19);
          return new Date().toLocaleString('en-US');
        }
        // return 'newAttachment';
      },
    },
    {
      title: t('general.action'),
      dataIndex: '',
      key: 'x',
      render: (text: any, record: any, index: number) => (
        <>
          <div style={{ display: 'flex' }}>
            {record.path !== '' && (
              <EyeOutlined style={{ fontSize: 18, marginInlineEnd: 10 }} onClick={() => handlePreview(record)} />
            )}
            <span>
              {record.path !== '' && (
                <DownloadOutlined
                  style={{ fontSize: '18px' }}
                  onClick={() =>
                    attachmentService.downloadAttachment({
                      path: record.path,
                      token: userState.token,
                      attachment: record,
                    })
                  }
                />
              )}
            </span>
            {deleteRow && !disableTable && (
              <span style={{ marginInlineStart: '10px' }}>
                <DeleteOutlined style={{ fontSize: '18px' }} onClick={() => deleteRow(attachments[index])} />
              </span>
            )}
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      <div>
        <Table id="attachments-in-form" pagination={false} columns={columns} dataSource={filteredDataSrc} />
        <Modal
          visible={previewVisible}
          onCancel={() => setPreviewVisible(false)}
          footer={null}
          width={'90vw'}
          style={{ top: 0, padding: 0 }}
          bodyStyle={{ height: '100vh', padding: 40 }}
          zIndex={9999}
        >
          {previewFile && renderPreviewContent(previewFile, userState.token)}
        </Modal>
      </div>
    </>
  );
};

export default FileTable;
